import clsx from "clsx";
import React, { ReactElement } from "react";
import {
  BaseCard,
  BaseCardBody,
  BaseCardFooter,
  BaseCardSanityImage,
  BaseCardSanityImageProps,
  BaseCardImageFooter,
  BaseCardTitle
} from "../BaseCard/BaseCard";
import { PromoBadges, PromoBadgeContent } from "../PromoBadges/PromoBadges";
import styles from "./SuiteCardBase.module.scss";

type SuiteCardBaseVariant = "default" | "selected" | "blurred";

export type SuiteCardBaseProps = {
  title?: string;
  subtitle?: ReactElement;
  image?: BaseCardSanityImageProps["image"];
  trackingName: string;
  className?: string;
  features: (string | ReactElement)[];
  actionButton?: ReactElement;
  detailsButton: ReactElement;
  variant?: SuiteCardBaseVariant;
  imageFooterComponent?: ReactElement;
  activePromos?: PromoBadgeContent[];
};

export const SuiteCardBase: React.FC<
  React.PropsWithChildren<SuiteCardBaseProps>
> = ({
  image,
  title,
  subtitle,
  trackingName,
  children,
  className,
  features,
  actionButton,
  detailsButton,
  imageFooterComponent,
  variant = "default",
  activePromos = []
}) => {
  return (
    <BaseCard
      className={clsx(className, {
        [styles.cardSelected]: variant == "selected",
        [styles.cardBlurred]: variant == "blurred"
      })}
      background="white"
      textAlign="center"
      trackingName={trackingName}
    >
      <BaseCardSanityImage
        image={image}
        ratio="card-compact"
        alt={title}
        extraComponent={
          activePromos.length > 0 ? (
            <PromoBadges promos={activePromos} className={styles.promoBox} />
          ) : undefined
        }
      />
      <div className={styles.imageFooter}>{imageFooterComponent}</div>
      <SuiteCardTitle>{title}</SuiteCardTitle>
      {subtitle}
      <BaseCardBody className={styles.body}>
        <ul className={styles.features}>
          {features.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
        <div className={styles.rightColumn}>{children}</div>
      </BaseCardBody>
      <BaseCardFooter className={styles.footer}>
        {detailsButton} {actionButton}
      </BaseCardFooter>
    </BaseCard>
  );
};

export const SuiteCardTitle: React.FC<React.PropsWithChildren> = ({
  children
}) => <BaseCardTitle className={styles.title}>{children}</BaseCardTitle>;

type SuiteCardImageFooterProps = {
  content: ReactElement;
  className?: string;
};

export const SuiteCardImageFooter: React.FC<
  React.PropsWithChildren<SuiteCardImageFooterProps>
> = ({ content, className, children }) => (
  <BaseCardImageFooter className={className}>
    {content}
    {children}
  </BaseCardImageFooter>
);
